// HTTP client utilities for API communication
export class ApiError extends Error {
    status;
    response;
    constructor(message, status, response) {
        super(message);
        this.status = status;
        this.response = response;
        this.name = "ApiError";
    }
}
class HttpClient {
    baseURL;
    defaultHeaders;
    constructor(baseURL = "", defaultHeaders = {}) {
        this.baseURL = baseURL;
        this.defaultHeaders = {
            "Content-Type": "application/json",
            ...defaultHeaders,
        };
    }
    buildURL(url, params) {
        const fullURL = url.startsWith("http") ? url : `${this.baseURL}${url}`;
        if (!params)
            return fullURL;
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            searchParams.append(key, String(value));
        });
        const separator = fullURL.includes("?") ? "&" : "?";
        return `${fullURL}${separator}${searchParams.toString()}`;
    }
    async handleResponse(response) {
        const contentType = response.headers.get("content-type");
        const isJson = contentType?.includes("application/json");
        let data;
        if (isJson) {
            data = await response.json();
        }
        else {
            data = (await response.text());
        }
        if (!response.ok) {
            const message = isJson && typeof data === "object" && data && "message" in data
                ? data.message
                : `HTTP ${response.status}: ${response.statusText}`;
            throw new ApiError(message, response.status, response);
        }
        return {
            data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
        };
    }
    async request(url, config = {}) {
        const { baseURL, timeout = 10000, params, headers = {}, ...fetchConfig } = config;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        try {
            const fullURL = this.buildURL(url, params);
            const response = await fetch(fullURL, {
                ...fetchConfig,
                headers: {
                    ...this.defaultHeaders,
                    ...headers,
                },
                signal: controller.signal,
            });
            return await this.handleResponse(response);
        }
        catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
                throw new ApiError("Request timeout", 408);
            }
            throw error;
        }
        finally {
            clearTimeout(timeoutId);
        }
    }
    async get(url, config) {
        return this.request(url, { ...config, method: "GET" });
    }
    async post(url, data, config) {
        return this.request(url, {
            ...config,
            method: "POST",
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async put(url, data, config) {
        return this.request(url, {
            ...config,
            method: "PUT",
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async patch(url, data, config) {
        return this.request(url, {
            ...config,
            method: "PATCH",
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async delete(url, config) {
        return this.request(url, { ...config, method: "DELETE" });
    }
    setAuthToken(token) {
        this.defaultHeaders.Authorization = `Bearer ${token}`;
    }
    removeAuthToken() {
        delete this.defaultHeaders.Authorization;
    }
    setBaseURL(baseURL) {
        this.baseURL = baseURL;
    }
}
// Create default instance
export const httpClient = new HttpClient();
// Export factory function for creating custom instances
export function createHttpClient(baseURL, defaultHeaders) {
    return new HttpClient(baseURL, defaultHeaders);
}
// Convenience functions using default instance
export const api = {
    get: (url, config) => httpClient.get(url, config),
    post: (url, data, config) => httpClient.post(url, data, config),
    put: (url, data, config) => httpClient.put(url, data, config),
    patch: (url, data, config) => httpClient.patch(url, data, config),
    delete: (url, config) => httpClient.delete(url, config),
    setAuthToken: (token) => httpClient.setAuthToken(token),
    removeAuthToken: () => httpClient.removeAuthToken(),
    setBaseURL: (baseURL) => httpClient.setBaseURL(baseURL),
};
